// Default-business shape (US-105) and form helpers shared by the create
// form, the editor, and the AI-scan review flow.
//
// Mirrors the prototype `DEFAULT_BUSINESS` (`prototypes_and_docs/ls-business.jsx:7-32`)
// while staying inside the `@barel/core` schema contract.

import type { Business } from '@barel/api-client/react';

export type BusinessDraft = Omit<
  Business,
  'id' | 'userId' | 'createdAt' | 'updatedAt' | 'archivedAt'
>;

export const PRICING_MODEL_KEYS = [
  'subscription',
  'one_shot',
  'commission',
  'quote',
  'freemium',
  'license',
  'retainer',
  'usage',
] as const;
export type PricingModelKey = (typeof PRICING_MODEL_KEYS)[number];

export const DEFAULT_BUSINESS: BusinessDraft = {
  linkedinAccountId: null,
  name: '',
  description: '',
  website: '',
  socials: [],
  email: '',
  phone: '',
  calendarUrl: '',
  contactPriority: 'calendar_first',
  personas: [],
  pains: [],
  valueProps: [],
  hotSignals: [],
  businessNature: '',
  pricingModels: [],
  pricingProducts: [],
  pricingPolicy: 'on_demand',
  questionGuide: [],
  bannedWords: [],
  salesRules: [],
  lastScanSuggestion: null,
  lastScanRunId: null,
};

/**
 * Auto-fix `contactPriority` when the available channels change. Mirrors
 * the prototype rule: a priority that references a channel the user just
 * cleared falls back to the next-best valid option.
 */
export function resolveContactPriority(
  draft: Pick<BusinessDraft, 'calendarUrl' | 'phone' | 'contactPriority'>,
): BusinessDraft['contactPriority'] {
  const hasCalendar = Boolean(draft.calendarUrl?.trim());
  const hasPhone = Boolean(draft.phone?.trim());
  if (hasCalendar && hasPhone) {
    if (draft.contactPriority === 'calendar_only' || draft.contactPriority === 'phone_only') {
      return 'calendar_first';
    }
    return draft.contactPriority;
  }
  if (hasCalendar && !hasPhone) return 'calendar_only';
  if (!hasCalendar && hasPhone) return 'phone_only';
  return 'calendar_first';
}

/** Auto-detect a social `kind` from a URL. */
export function detectSocialKind(url: string): string {
  const lower = url.toLowerCase();
  if (lower.includes('linkedin.com')) return 'linkedin';
  if (lower.includes('twitter.com') || lower.includes('x.com')) return 'twitter';
  if (lower.includes('instagram.com')) return 'instagram';
  if (lower.includes('tiktok.com')) return 'tiktok';
  if (lower.includes('youtube.com') || lower.includes('youtu.be')) return 'youtube';
  if (lower.includes('facebook.com')) return 'facebook';
  return 'other';
}
